import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  Button,
  Table,
  Space,
  Input,
  Modal,
  Form,
  InputNumber,
  Switch,
  Select,
  Message,
  Popconfirm,
  Tooltip,
  DatePicker,
  Empty,
  Alert,
  Collapse,
  Pagination,
} from '@arco-design/web-react'
import {
  IconPlus,
  IconCopy,
  IconEdit,
  IconDelete,
  IconEye,
  IconSearch,
  IconRefresh,
  IconCheckCircleFill,
  IconPause,
  IconPlayArrow,
} from '@arco-design/web-react/icon'
import { useQuery } from '@tanstack/react-query'
import {
  useTokens,
  useCreateToken,
  useUpdateToken,
  useUpdateTokenStatus,
  useDeleteToken,
  useBatchDeleteTokens,
} from '@/hooks/useTokens'
import { getApiKeySecretApi } from '@/api/apiKeys'
import { getUserModelsApi } from '@/api/user'
import { getUserSelfGroupsApi } from '@/api/group'
import { useSecureVerification } from '@/hooks/useSecureVerification'
import SecureVerifyModal from '@/components/Common/SecureVerifyModal'
import {
  renderTokenStatus,
  renderTimestamp,
  renderQuotaRemainTotal,
  renderMaskedApiKey,
  renderModelLimits,
  Pill,
  renderGroupTag,
} from '@/utils/render'
import { TOKEN_STATUS } from '@/utils/constants'
import {
  renderQuota,
  quotaToDisplayAmount,
  displayAmountToQuota,
} from '@/utils/quota'
import { getFormValidateErrorMessage } from '@/utils/helpers'
import type { ApiKey } from '@/types'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { modalWidth } from '@/utils/responsive'

interface ApiKeyFormValues {
  name: string
  unlimited_quota?: boolean
  remain_quota_display?: number
  expired_never?: boolean
  expired_time?: number
  model_limits_enabled?: boolean
  model_limits?: string[]
  group?: string
  allow_ips?: string
  count?: number
  cross_group_retry?: boolean
}

function generateRandomSuffix(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let s = ''
  for (let i = 0; i < 6; i++) {
    s += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return s
}

function readCrossGroupRetry(row: ApiKey): boolean {
  const r = row as ApiKey & { cross_group_retry?: boolean }
  return !!(r.crossGroupRetry ?? r.cross_group_retry)
}

/** 列表接口返回 snake_case，与表单/类型 camelCase 对齐 */
function normalizeApiKeyRow(raw: ApiKey | Record<string, unknown>): ApiKey {
  const r = raw as Record<string, unknown>
  const pickNum = (a: string, b: string): number => {
    const v = r[a] ?? r[b]
    if (typeof v === 'number' && !Number.isNaN(v)) return v
    if (typeof v === 'string' && v !== '') return Number(v) || 0
    return 0
  }
  const pickBool = (a: string, b: string): boolean => {
    const v = r[a] ?? r[b]
    return !!v
  }
  const pickStr = (a: string, b: string): string => {
    const v = r[a] ?? r[b]
    return v == null ? '' : String(v)
  }
  const expRaw = r.expired_time ?? r.expiredTime
  const expiredTime =
    expRaw === -1 || expRaw === '-1' ? -1 : typeof expRaw === 'number' ? expRaw : Number(expRaw) || -1

  const mlRaw = r.model_limits ?? r.modelLimits
  let modelLimits: string[] = []
  if (Array.isArray(mlRaw)) modelLimits = mlRaw.map(String)
  else if (typeof mlRaw === 'string' && mlRaw)
    modelLimits = mlRaw.split(',').map((s) => s.trim()).filter(Boolean)

  const allow = r.allow_ips ?? r.allowIps

  return {
    id: pickNum('id', 'id'),
    name: pickStr('name', 'name'),
    key: pickStr('key', 'key'),
    status: pickNum('status', 'status'),
    remainQuota: pickNum('remain_quota', 'remainQuota'),
    usedQuota: pickNum('used_quota', 'usedQuota'),
    unlimitedQuota: pickBool('unlimited_quota', 'unlimitedQuota'),
    modelLimitsEnabled: pickBool('model_limits_enabled', 'modelLimitsEnabled'),
    modelLimits,
    allowIps: allow == null ? undefined : String(allow),
    createdTime: pickNum('created_time', 'createdTime'),
    accessedTime: pickNum('accessed_time', 'accessedTime'),
    expiredTime,
    group: pickStr('group', 'group') || undefined,
    crossGroupRetry: pickBool('cross_group_retry', 'crossGroupRetry'),
    cross_group_retry: pickBool('cross_group_retry', 'crossGroupRetry'),
  }
}

const unitSymbol = () => {
  const sample = renderQuota(1)
  const m = sample.match(/^[^\d.]+/)
  return m ? m[0].trim() : ''
}

function FormSectionTitle({ children }: { children: ReactNode }) {
  return (
    <div className="text-[13px] font-semibold text-[var(--console-text-h)] mb-3 pb-2 border-b border-[var(--console-border)]">
      {children}
    </div>
  )
}

interface ApiKeyCardActionsProps {
  row: ApiKey
  onCopy: (row: ApiKey) => void
  onView: (row: ApiKey) => void
  onEdit: (row: ApiKey) => void
  onDelete: (row: ApiKey) => void
  onToggleStatus: (row: ApiKey) => void
  statusLoading?: boolean
}

function ApiKeyMobileCard({
  row,
  onCopy,
  onView,
  onEdit,
  onDelete,
  onToggleStatus,
  statusLoading,
}: ApiKeyCardActionsProps) {
  const { t } = useTranslation()

  return (
    <div className="rounded-xl border border-[var(--console-border)] p-4 space-y-3 bg-white shadow-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[var(--console-text-h)] truncate">{row.name || '-'}</div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {renderTokenStatus(row.status)}
            {row.group ? renderGroupTag(row.group) : null}
          </div>
        </div>
      </div>
      <div className="text-xs text-[var(--console-text-muted)] font-mono">{renderMaskedApiKey(row.key)}</div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--console-text-muted)]">
        {row.unlimitedQuota ? (
          <Pill variant="success">{t('tt.apiKey.unlimitedQuota')}</Pill>
        ) : (
          <div className="min-w-0 flex-1">{renderQuotaRemainTotal(row.remainQuota, row.usedQuota ?? 0)}</div>
        )}
      </div>
      <div className="text-xs">{renderModelLimits(row.modelLimitsEnabled, row.modelLimits)}</div>
      <div className="flex flex-wrap gap-1.5 text-xs text-[var(--console-text-muted)]">
        <span>{row.createdTime ? renderTimestamp(row.createdTime) : '-'}</span>
        <span className="text-[var(--console-text-subtle)]">·</span>
        {row.expiredTime === -1 ? (
          <span className="text-[var(--console-text-subtle)]">{t('tt.apiKey.expiredNever')}</span>
        ) : (
          <span>{renderTimestamp(row.expiredTime)}</span>
        )}
      </div>
      <div className="flex flex-wrap gap-1">
        {row.status === TOKEN_STATUS.ENABLED ? (
          <Button
            size="mini"
            type="outline"
            icon={<IconPause />}
            loading={statusLoading}
            onClick={() => onToggleStatus(row)}
          >
            {t('tt.apiKey.disable')}
          </Button>
        ) : row.status === TOKEN_STATUS.DISABLED ? (
          <Button
            size="mini"
            type="outline"
            icon={<IconPlayArrow />}
            loading={statusLoading}
            onClick={() => onToggleStatus(row)}
          >
            {t('tt.apiKey.enable')}
          </Button>
        ) : null}
        <Button size="mini" type="outline" icon={<IconCopy />} aria-label={t('复制密钥')} onClick={() => onCopy(row)}>
          {t('复制')}
        </Button>
        <Button size="mini" type="outline" icon={<IconEye />} aria-label={t('tt.apiKey.viewKey')} onClick={() => onView(row)}>
          {t('查看')}
        </Button>
        <Button size="mini" type="outline" icon={<IconEdit />} aria-label={t('tt.apiKey.edit')} onClick={() => onEdit(row)}>
          {t('编辑')}
        </Button>
        <Popconfirm title={t('tt.apiKey.deleteConfirm')} onOk={() => onDelete(row)}>
          <Button size="mini" type="outline" status="danger" icon={<IconDelete />} aria-label={t('删除')}>
            {t('删除')}
          </Button>
        </Popconfirm>
      </div>
    </div>
  )
}

export default function ApiKeyPage() {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [editing, setEditing] = useState<ApiKey | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form] = Form.useForm<ApiKeyFormValues>()
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([])
  const [recentCreatedKey, setRecentCreatedKey] = useState<string>('')
  const tableAreaRef = useRef<HTMLDivElement>(null)
  const [tableBodyScrollY, setTableBodyScrollY] = useState(440)

  const measureTableBody = useCallback(() => {
    const el = tableAreaRef.current
    if (!el) return
    const h = el.clientHeight
    const theadReserve = 50
    setTableBodyScrollY(Math.max(200, Math.floor(h - theadReserve)))
  }, [])

  useLayoutEffect(() => {
    measureTableBody()
    const el = tableAreaRef.current
    const ro = new ResizeObserver(() => measureTableBody())
    if (el) ro.observe(el)
    window.addEventListener('resize', measureTableBody)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measureTableBody)
    }
  }, [measureTableBody])

  const { data, isLoading, refetch, isFetching } = useTokens({ page, page_size: pageSize })
  const createMut = useCreateToken()
  const updateMut = useUpdateToken()
  const statusMut = useUpdateTokenStatus()
  const deleteMut = useDeleteToken()
  const batchDeleteMut = useBatchDeleteTokens()

  const { runSecure, verifyVisible, handleSuccess, handleCancel } = useSecureVerification()

  // 模型/分组候选
  const modelsQuery = useQuery({
    queryKey: ['user-models'],
    queryFn: () => getUserModelsApi(),
    staleTime: 5 * 60_000,
  })
  const groupsQuery = useQuery({
    queryKey: ['user-self-groups'],
    queryFn: () => getUserSelfGroupsApi().catch(() => ({} as Record<string, unknown>)),
    staleTime: 5 * 60_000,
  })

  const modelOptions = useMemo(
    () => (modelsQuery.data || []).map((m) => ({ label: m, value: m })),
    [modelsQuery.data]
  )
  const groupOptions = useMemo(
    () =>
      Object.keys(groupsQuery.data || {}).map((g) => ({
        label: g,
        value: g,
      })),
    [groupsQuery.data]
  )

  const normalizedList = useMemo(
    () => (data?.items || []).map((row) => normalizeApiKeyRow(row as ApiKey)),
    [data?.items]
  )

  const items = useMemo(() => {
    if (!keyword) return normalizedList
    const kw = keyword.toLowerCase()
    return normalizedList.filter((x) => x.name?.toLowerCase().includes(kw))
  }, [normalizedList, keyword])

  useLayoutEffect(() => {
    if (!isLoading) measureTableBody()
  }, [isLoading, items.length, measureTableBody])

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      unlimited_quota: false,
      expired_never: true,
      model_limits_enabled: false,
      remain_quota_display: 1,
      count: 1,
      cross_group_retry: false,
    })
    setModalOpen(true)
  }

  const openEdit = (row: ApiKey) => {
    setEditing(row)
    form.setFieldsValue({
      name: row.name,
      unlimited_quota: row.unlimitedQuota,
      remain_quota_display: quotaToDisplayAmount(row.remainQuota),
      expired_never: row.expiredTime === -1,
      expired_time:
        row.expiredTime && row.expiredTime > 0 ? row.expiredTime * 1000 : undefined,
      model_limits_enabled: row.modelLimitsEnabled,
      model_limits: row.modelLimits || [],
      group: row.group || undefined,
      allow_ips: row.allowIps || '',
      cross_group_retry: readCrossGroupRetry(row),
    })
    setModalOpen(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validate()
      const remainQuota = values.unlimited_quota
        ? 0
        : displayAmountToQuota(values.remain_quota_display || 0)
      const expiredTime = values.expired_never
        ? -1
        : values.expired_time
          ? Math.floor(values.expired_time / 1000)
          : -1
      const modelsJoined = (values.model_limits || []).filter(Boolean).join(',')
      const modelLimitsEnabled = !!values.model_limits_enabled && modelsJoined.length > 0
      const allowIpsRaw = (values.allow_ips || '').trim()
      const groupVal = values.group || ''
      const payloadBase = {
        unlimited_quota: !!values.unlimited_quota,
        remain_quota: remainQuota,
        expired_time: expiredTime,
        model_limits_enabled: modelLimitsEnabled,
        model_limits: modelsJoined,
        group: groupVal,
        allow_ips: allowIpsRaw,
        cross_group_retry: groupVal === 'auto' ? !!values.cross_group_retry : false,
      }

      if (editing) {
        await updateMut.mutateAsync({
          id: editing.id,
          name: values.name.trim(),
          ...payloadBase,
        })
        Message.success(t('tt.apiKey.updated'))
      } else {
        const count = Math.max(1, Math.min(100, values.count || 1))
        const baseName = values.name.trim()
        for (let i = 0; i < count; i++) {
          const name = count > 1 && i > 0 ? `${baseName}-${generateRandomSuffix()}` : baseName
          await createMut.mutateAsync({ name, ...payloadBase })
        }
        Message.success(count > 1 ? t('tt.apiKey.createdMany', { count }) : t('tt.apiKey.created'))
      }
      setModalOpen(false)
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    }
  }

  const handleCopy = async (row: ApiKey) => {
    try {
      const result = await runSecure(() => getApiKeySecretApi(row.id))
      const key = (result as { key: string })?.key
      if (!key) throw new Error(t('tt.apiKey.fetchKeyFailed'))
      await navigator.clipboard.writeText(`sk-${key}`)
      setRecentCreatedKey(String(row.id))
      Message.success(t('tt.apiKey.copiedClipboard'))
      setTimeout(() => setRecentCreatedKey(''), 2000)
    } catch (err) {
      if (err instanceof Error && err.message !== t('用户取消了二次验证')) {
        Message.error(err.message || t('复制失败'))
      }
    }
  }

  const handleViewKey = async (row: ApiKey) => {
    try {
      const result = await runSecure(() => getApiKeySecretApi(row.id))
      const key = (result as { key: string })?.key
      if (!key) throw new Error(t('tt.apiKey.fetchKeyFailed'))
      Modal.info({
        title: t('tt.apiKey.viewKey'),
        style: { width: modalWidth(560) },
        content: (
          <div className="space-y-3">
            <Alert
              type="warning"
              content={t('tt.apiKey.keyOnceWarning')}
              className="!bg-[var(--brand-primary-light)] !border-[var(--console-border)]"
            />
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all text-xs p-3 bg-[var(--console-page-bg)] rounded-lg border border-[var(--console-border)] font-mono">
                {`sk-${key}`}
              </code>
              <Button
                size="small"
                type="primary"
                icon={<IconCopy />}
                aria-label={t('复制密钥')}
                onClick={async () => {
                  await navigator.clipboard.writeText(`sk-${key}`)
                  Message.success(t('common.copied'))
                }}
              >
                {t('复制')}
              </Button>
            </div>
          </div>
        ),
      })
    } catch (err) {
      if (err instanceof Error && err.message !== t('用户取消了二次验证')) {
        Message.error(err.message || t('tt.apiKey.viewKeyFailed'))
      }
    }
  }

  const handleToggleStatus = async (row: ApiKey) => {
    const nextStatus =
      row.status === TOKEN_STATUS.ENABLED ? TOKEN_STATUS.DISABLED : TOKEN_STATUS.ENABLED
    try {
      await statusMut.mutateAsync({ id: row.id, status: nextStatus })
      Message.success(
        nextStatus === TOKEN_STATUS.ENABLED ? t('tt.apiKey.enabled') : t('tt.apiKey.disabled')
      )
    } catch (err: unknown) {
      if (err instanceof Error) Message.error(err.message || t('操作失败'))
    }
  }

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) return
    try {
      await batchDeleteMut.mutateAsync(selectedRowKeys)
      Message.success(t('tt.apiKey.deletedMany', { count: selectedRowKeys.length }))
      setSelectedRowKeys([])
    } catch (err: unknown) {
      if (err instanceof Error) Message.error(err.message || t('批量删除失败'))
    }
  }

  const columns = useMemo(
    () => [
    {
      title: t('名称'),
      dataIndex: 'name',
      width: 160,
      fixed: 'left' as const,
      ellipsis: true,
      render: (v: string) => (
        <span className="text-sm text-[var(--console-text-h)] font-medium block min-w-0 truncate">{v || '-'}</span>
      ),
    },
    {
      title: t('状态'),
      dataIndex: 'status',
      width: 88,
      render: (status: number) => <span className="inline-flex">{renderTokenStatus(status)}</span>,
    },
    {
      title: t('tt.apiKey.quotaRemainTotal'),
      width: 160,
      render: (_: unknown, row: ApiKey) =>
        row.unlimitedQuota ? (
          <Pill variant="success">{t('tt.apiKey.unlimitedQuota')}</Pill>
        ) : (
          renderQuotaRemainTotal(row.remainQuota, row.usedQuota ?? 0)
        ),
    },
    {
      title: t('分组'),
      dataIndex: 'group',
      width: 100,
      ellipsis: true,
      render: (v?: string) =>
        v ? (
          renderGroupTag(v)
        ) : (
          <span className="text-[var(--console-text-subtle)] text-xs">{t('tt.apiKey.inheritUser')}</span>
        ),
    },
    {
      title: t('tt.apiKey.keyColumn'),
      dataIndex: 'key',
      width: 140,
      render: (_: unknown, row: ApiKey) => renderMaskedApiKey(row.key),
    },
    {
      title: t('tt.apiKey.createdAt'),
      width: 150,
      render: (_: unknown, row: ApiKey) => (
        <span className="block min-w-0 text-xs text-[var(--console-text-muted)] [&_span]:truncate">
          {row.createdTime ? renderTimestamp(row.createdTime) : '-'}
        </span>
      ),
    },
    {
      title: t('tt.apiKey.expiredAt'),
      width: 150,
      render: (_: unknown, row: ApiKey) =>
        row.expiredTime === -1 ? (
          <span className="text-xs text-[var(--console-text-subtle)]">{t('tt.apiKey.expiredNever')}</span>
        ) : (
          <span className="block text-xs text-[var(--console-text-muted)] min-w-0 truncate">
            {renderTimestamp(row.expiredTime)}
          </span>
        ),
    },
    {
      title: t('操作'),
      key: 'actions',
      width: 168,
      fixed: 'right' as const,
      render: (_: unknown, row: ApiKey) => (
        <Space size={2}>
          {row.status === TOKEN_STATUS.ENABLED ? (
            <Tooltip content={t('tt.apiKey.disable')}>
              <Button
                type="text"
                size="small"
                aria-label={t('tt.apiKey.disable')}
                icon={<IconPause />}
                loading={statusMut.isPending}
                onClick={() => handleToggleStatus(row)}
              />
            </Tooltip>
          ) : row.status === TOKEN_STATUS.DISABLED ? (
            <Tooltip content={t('tt.apiKey.enable')}>
              <Button
                type="text"
                size="small"
                aria-label={t('tt.apiKey.enable')}
                icon={<IconPlayArrow />}
                loading={statusMut.isPending}
                onClick={() => handleToggleStatus(row)}
              />
            </Tooltip>
          ) : null}
          <Tooltip content={t('复制密钥')}>
            <Button
              type="text"
              size="small"
              aria-label={t('复制密钥')}
              icon={
                String(row.id) === recentCreatedKey ? (
                  <IconCheckCircleFill className="!text-[#10B981]" />
                ) : (
                  <IconCopy />
                )
              }
              onClick={() => handleCopy(row)}
            />
          </Tooltip>
          <Tooltip content={t('tt.apiKey.viewKey')}>
            <Button
              type="text"
              size="small"
              aria-label={t('tt.apiKey.viewKey')}
              icon={<IconEye />}
              onClick={() => handleViewKey(row)}
            />
          </Tooltip>
          <Tooltip content={t('编辑')}>
            <Button
              type="text"
              size="small"
              aria-label={t('tt.apiKey.edit')}
              icon={<IconEdit />}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
          <Popconfirm
            title={t('tt.apiKey.deleteConfirm')}
            onOk={() =>
              deleteMut
                .mutateAsync(row.id)
                .then(() => Message.success(t('tt.apiKey.deleted')))
                .catch((e: Error) => Message.error(e.message || t('删除失败')))
            }
          >
            <Tooltip content={t('删除')}>
              <Button
                type="text"
                size="small"
                status="danger"
                aria-label={t('删除')}
                icon={<IconDelete />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ],
    [t, recentCreatedKey, deleteMut, statusMut.isPending],
  )

  useEffect(() => {
    setSelectedRowKeys((prev) =>
      prev.filter((id) => items.some((x) => x.id === id))
    )
  }, [items])

  return (
    <div className="space-y-4">
      <Card className="tt-card !rounded-2xl" bordered={false} bodyStyle={{ padding: '12px 16px' }}>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder={t('tt.apiKey.searchCurrentList')}
            value={keyword}
            onChange={setKeyword}
            className="w-full sm:max-w-[320px]"
            prefix={<IconSearch />}
            allowClear
          />
          <div className="flex-1 min-w-[8px]" />
          <div className="w-full sm:w-auto flex flex-wrap items-center gap-2 justify-start sm:justify-end flex-shrink-0">
            <Button
              icon={<IconRefresh />}
              onClick={() => refetch()}
              loading={isFetching && !isLoading}
              aria-label={t('刷新')}
            >
              {t('刷新')}
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={openCreate}>
              {t('tt.apiKey.create')}
            </Button>
          </div>
        </div>
      </Card>

      {selectedRowKeys.length > 0 && (
        <Card
          className="tt-card !rounded-2xl !border-[var(--brand-primary-mid)]/30"
          bordered={false}
          bodyStyle={{ padding: '12px 16px', background: 'var(--brand-primary-light)' }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-[var(--console-text-body)]">
              {t('tt.apiKey.selectedCount', { count: selectedRowKeys.length })}
            </span>
            <Space>
              <Button size="small" onClick={() => setSelectedRowKeys([])}>
                {t('tt.apiKey.clearSelection')}
              </Button>
              <Popconfirm
                title={t('tt.apiKey.batchDeleteConfirm', { count: selectedRowKeys.length })}
                onOk={handleBatchDelete}
              >
                <Button
                  size="small"
                  status="danger"
                  loading={batchDeleteMut.isPending}
                  icon={<IconDelete />}
                >
                  {t('tt.apiKey.batchDelete')}
                </Button>
              </Popconfirm>
            </Space>
          </div>
        </Card>
      )}

      <Card
        className={`tt-card !rounded-2xl flex flex-col ${
          isMobile ? 'min-h-0' : 'h-[calc(100vh-12.5rem)] min-h-[360px]'
        }`}
        bordered={false}
        bodyStyle={{
          padding: isMobile ? 12 : 0,
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          overflow: isMobile ? 'visible' : 'hidden',
          height: isMobile ? 'auto' : '100%',
        }}
      >
        {isMobile ? (
          <div className="space-y-3">
            {isLoading ? (
              <div className="py-10 text-center text-sm text-gray-500">{t('加载中...')}</div>
            ) : items.length === 0 ? (
              <Empty
                description={
                  <span className="text-sm text-gray-500">
                    {keyword ? t('tt.apiKey.emptyNoMatch') : t('tt.apiKey.emptyCreate')}
                  </span>
                }
              />
            ) : (
              items.map((row) => (
                <ApiKeyMobileCard
                  key={row.id}
                  row={row}
                  onCopy={handleCopy}
                  onView={handleViewKey}
                  onEdit={openEdit}
                  onToggleStatus={handleToggleStatus}
                  statusLoading={statusMut.isPending}
                  onDelete={(r) =>
                    deleteMut
                      .mutateAsync(r.id)
                      .then(() => Message.success(t('tt.apiKey.deleted')))
                      .catch((e: Error) => Message.error(e.message || t('删除失败')))
                  }
                />
              ))
            )}
          </div>
        ) : (
        <div ref={tableAreaRef} className="min-h-0 min-w-0 flex-1 overflow-hidden">
          <Table
            tableLayoutFixed
            rowKey="id"
            loading={isLoading}
            columns={columns}
            data={items}
            pagination={false}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys as number[]),
            }}
            noDataElement={
              <div className="py-10">
                <Empty
                  description={
                    <span className="text-sm text-gray-500">
                      {keyword ? t('tt.apiKey.emptyNoMatch') : t('tt.apiKey.emptyCreateDesktop')}
                    </span>
                  }
                />
              </div>
            }
            scroll={{ y: tableBodyScrollY, x: 1200 }}
            stripe={false}
            borderCell={false}
            className="api-keys-table [&_.arco-table-container]:max-w-full [&_.arco-table-th]:!py-3 [&_.arco-table-td]:!py-3 [&_.arco-table-td]:!h-12"
          />
        </div>
        )}
        <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-x-4 gap-y-2 px-4 py-3 border-t border-[var(--console-border)] bg-[var(--console-hover-row)] rounded-b-2xl">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={data?.total || 0}
            showTotal={(total, range) =>
              total > 0
                ? t('tt.apiKey.pagination', { from: range[0], to: range[1], total })
                : t('tt.apiKey.paginationTotalOnly', { total })
            }
            sizeCanChange
            pageSizeChangeResetCurrent
            sizeOptions={[10, 20, 50, 100]}
            onChange={(p, ps) => {
              setPage(p)
              setPageSize(ps)
            }}
          />
        </div>
      </Card>

      {/* 创建 / 编辑弹窗 */}
      <Modal
        title={editing ? t('tt.apiKey.edit') : t('tt.apiKey.create')}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={createMut.isPending || updateMut.isPending}
        style={{ width: modalWidth(560) }}
        className="[&_.arco-modal-content]:max-h-[min(90vh,720px)] [&_.arco-modal-content]:overflow-y-auto"
        okText={editing ? t('保存') : t('创建')}
        cancelText={t('取消')}
        unmountOnExit
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          initialValues={{ unlimited_quota: false, expired_never: true, count: 1 }}
        >
          <FormSectionTitle>{t('tt.apiKey.sectionBasic')}</FormSectionTitle>
          <Form.Item
            field="name"
            label={t('名称')}
            rules={[{ required: true, message: t('请输入密钥名称') }]}
          >
            <Input placeholder={t('tt.apiKey.namePlaceholder')} maxLength={50} showWordLimit />
          </Form.Item>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Form.Item
                field="unlimited_quota"
                label={t('tt.apiKey.quotaLabel')}
                triggerPropName="checked"
                tooltip={t('关闭后需填写剩余额度；开启则本密钥不受额度上限限制（仍受账户总额度约束）')}
              >
                <Switch checkedText={t('tt.apiKey.unlimitedShort')} uncheckedText={t('自定义')} />
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {(values) =>
                  !values.unlimited_quota && (
                    <Form.Item
                      field="remain_quota_display"
                      label={`${t('tt.apiKey.remainQuota')}${unitSymbol() ? ` (${unitSymbol()})` : ''}`}
                      rules={[{ required: true, message: t('请输入额度') }]}
                    >
                      <InputNumber
                        placeholder={t('tt.apiKey.remainQuotaPlaceholder')}
                        min={0}
                        step={1}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  )
                }
              </Form.Item>
            </div>
            <div className="space-y-1">
              <Form.Item
                field="expired_never"
                label={t('tt.apiKey.expireLabel')}
                triggerPropName="checked"
              >
                <Switch checkedText={t('tt.apiKey.expiredNever')} uncheckedText={t('指定时间')} />
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {(values) =>
                  !values.expired_never && (
                    <>
                      <Form.Item
                        field="expired_time"
                        label={t('tt.apiKey.expiredAt')}
                        rules={[{ required: true, message: t('请选择过期时间') }]}
                      >
                        <DatePicker
                          showTime
                          style={{ width: '100%' }}
                          placeholder={t('请选择过期时间')}
                          disabledDate={(date) => !!date && date.valueOf() < Date.now()}
                        />
                      </Form.Item>
                      <div className="flex flex-wrap gap-1.5 pb-1">
                        <Button
                          type="outline"
                          size="mini"
                          onClick={() =>
                            form.setFieldsValue({ expired_never: true, expired_time: undefined })
                          }
                        >
                          {t('tt.apiKey.expiredNever')}
                        </Button>
                        <Button
                          type="outline"
                          size="mini"
                          onClick={() =>
                            form.setFieldsValue({
                              expired_never: false,
                              expired_time: Date.now() + 30 * 24 * 60 * 60 * 1000,
                            })
                          }
                        >
                          {t('一个月')}
                        </Button>
                        <Button
                          type="outline"
                          size="mini"
                          onClick={() =>
                            form.setFieldsValue({
                              expired_never: false,
                              expired_time: Date.now() + 24 * 60 * 60 * 1000,
                            })
                          }
                        >
                          {t('一天')}
                        </Button>
                        <Button
                          type="outline"
                          size="mini"
                          onClick={() =>
                            form.setFieldsValue({
                              expired_never: false,
                              expired_time: Date.now() + 60 * 60 * 1000,
                            })
                          }
                        >
                          {t('一小时')}
                        </Button>
                      </div>
                    </>
                  )
                }
              </Form.Item>
            </div>
          </div>

          {groupOptions.length > 0 && (
            <>
              <FormSectionTitle>{t('tt.apiKey.sectionAccess')}</FormSectionTitle>
              <Form.Item
                field="group"
                label={t('分组')}
                tooltip={t('tt.apiKey.groupHint')}
              >
                <Select
                  allowClear
                  placeholder={t('tt.apiKey.groupPlaceholder')}
                  options={groupOptions}
                />
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {(values) =>
                  values.group === 'auto' ? (
                    <Form.Item
                      field="cross_group_retry"
                      label={t('tt.apiKey.crossGroupRetry')}
                      triggerPropName="checked"
                      extra={
                        <span className="text-xs text-[var(--console-text-subtle)]">
                          {t('tt.apiKey.crossGroupRetryShort')}
                        </span>
                      }
                    >
                      <Switch />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
            </>
          )}

          <FormSectionTitle>{t('tt.apiKey.sectionAdvanced')}</FormSectionTitle>
          <Collapse
            bordered={false}
            defaultActiveKey={[]}
            className="bg-[var(--console-page-bg)] rounded-xl border border-[var(--console-border)] px-3"
          >
            <Collapse.Item
              name="advanced"
              header={
                <span className="text-sm font-medium text-[var(--brand-primary)]">
                  {t('tt.apiKey.advancedOptions', {
                    extra: !editing ? t('tt.apiKey.advancedOptionsExtra') : '',
                  })}
                </span>
              }
            >
              <div className="space-y-1 pt-1">
                <Form.Item
                  field="model_limits_enabled"
                  label={t('tt.apiKey.enableModelLimits')}
                  triggerPropName="checked"
                  tooltip={t('tt.apiKey.enableModelLimitsHint')}
                >
                  <Switch />
                </Form.Item>
                <Form.Item shouldUpdate noStyle>
                  {(values) =>
                    values.model_limits_enabled && (
                      <Form.Item
                        field="model_limits"
                        label={t('tt.apiKey.allowedModels')}
                        rules={[{ required: true, message: t('请选择至少一个模型') }]}
                      >
                        <Select
                          mode="multiple"
                          placeholder={t('tt.apiKey.allowedModelsPlaceholder')}
                          allowClear
                          options={modelOptions}
                          loading={modelsQuery.isLoading}
                          maxTagCount={4}
                        />
                      </Form.Item>
                    )
                  }
                </Form.Item>

                <Form.Item
                  field="allow_ips"
                  label={t('tt.apiKey.allowIps')}
                  extra={
                    <span className="text-xs text-gray-500">
                      {t('tt.apiKey.allowIpsHint')}
                    </span>
                  }
                >
                  <Input.TextArea
                    placeholder={t('tt.apiKey.allowIpsPlaceholder')}
                    rows={3}
                    autoSize={{ minRows: 3, maxRows: 6 }}
                    className="rounded-lg font-mono text-sm"
                  />
                </Form.Item>

                {!editing && (
                  <Form.Item
                    field="count"
                    label={t('tt.apiKey.batchCreateCountLabel')}
                    tooltip={t('tt.apiKey.batchCreateHint')}
                  >
                    <InputNumber
                      min={1}
                      max={100}
                      step={1}
                      style={{ width: '100%' }}
                      placeholder={t('tt.apiKey.batchCreateDefault')}
                    />
                  </Form.Item>
                )}
              </div>
            </Collapse.Item>
          </Collapse>
        </Form>
      </Modal>

      <SecureVerifyModal
        visible={verifyVisible}
        onCancel={handleCancel}
        onSuccess={handleSuccess}
        title={t('该操作涉及敏感信息，请完成安全验证')}
      />
    </div>
  )
}
